import { useEffect, useRef, useState } from 'preact/hooks';
import {
  detectFromEnvironment,
  type Context,
  type DetectionResult
} from './detectContext';
import { ShareIcon, AddToHomeIcon, BellIcon } from './icons';
import { requestPermissionAndSubscribe, ensurePushSubscription } from '../../push/subscribe';
import { getSupabaseConfig } from '../../config';
import { savePushSubscription } from '../../push/save';
import { getSupabase } from '../../lib/supabaseClient';
import { allSetFromSave } from './allSet';
import { requestTestNotification } from '../../push/testStub';
import './install.css';

function ForcedContext(): Context | undefined {
  if (typeof window === 'undefined') return undefined;
  const sp = new URLSearchParams(window.location.search);
  const forced = sp.get('forceContext') as Context | null;
  // Gate forcing on production: ignore on the production hostname so QA can use it
  // on localhost and preview URLs, but it won't alter the real user flow.
  const hostname = window.location.hostname;
  const isProductionHost = hostname === 'shores-park-club.pages.dev';
  if (isProductionHost) return undefined;
  return forced ?? undefined;
}

function useDetection(): DetectionResult {
  const base = detectFromEnvironment();
  const forced = ForcedContext();
  return forced ? { ...base, context: forced } : base;
}

function Section(props: { title: string; children: preact.ComponentChildren }) {
  return (
    <section class="install-safe">
      <div class="install-card">
        <h2 class="install-title">{props.title}</h2>
        <div class="install-body">{props.children}</div>
      </div>
    </section>
  );
}

export function InstallFlow() {
  const detection = useDetection();
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'subscribing' }
    | { kind: 'success' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function enableNotifications() {
    setState({ kind: 'subscribing' });
    const result = await requestPermissionAndSubscribe();
    if (result.kind === 'blocked') {
      setState({ kind: 'error', message: 'Permission was not granted.' });
      return;
    }
    if (result.kind === 'error') {
      console.error('[push subscribe] error', result.error);
      setState({ kind: 'error', message: 'Something went wrong. Please try again.' });
      return;
    }
    const { url, anonKey } = getSupabaseConfig();
    const sb = getSupabase();
    const accessToken = sb ? (await sb.auth.getSession()).data.session?.access_token ?? null : null;
    const profileId = sb ? (await sb.auth.getUser()).data.user?.id ?? null : null;
    const saveRes = await savePushSubscription({
      supabaseUrl: url,
      anonKey,
      accessToken: accessToken ?? undefined,
      record: {
        profile_id: profileId ?? '',
        endpoint: result.endpoint,
        p256dh: result.p256dh,
        auth: result.auth,
        user_agent: navigator.userAgent,
        enabled: true
      }
    });
    const verdict = allSetFromSave(saveRes, !!url && !!anonKey, !!accessToken && !!profileId);
    if (verdict.allSet) {
      setState({ kind: 'success' });
    } else {
      setState({
        kind: 'error',
        message:
          verdict.reason ||
          'We could not save your notification subscription. Please try again.'
      });
    }
  }

  async function alreadyEnabledSaveIfNeeded() {
    // If granted, make sure we have a subscription and attempt a save
    const res = await ensurePushSubscription();
    if (res.kind === 'already' || res.kind === 'subscribed') {
      const { url, anonKey } = getSupabaseConfig();
      const sb = getSupabase();
      const accessToken = sb ? (await sb.auth.getSession()).data.session?.access_token ?? null : null;
      const profileId = sb ? (await sb.auth.getUser()).data.user?.id ?? null : null;
      const saveRes = await savePushSubscription({
        supabaseUrl: url,
        anonKey,
        accessToken: accessToken ?? undefined,
        record: {
          profile_id: profileId ?? '',
          endpoint: res.endpoint,
          p256dh: res.p256dh,
          auth: res.auth,
          user_agent: navigator.userAgent
        }
      });
      const verdict = allSetFromSave(saveRes, !!url && !!anonKey, !!accessToken && !!profileId);
      if (verdict.allSet) {
        setState({ kind: 'success' });
      } else if (verdict.reason) {
        setState({ kind: 'error', message: verdict.reason });
      }
    }
  }

  // One-time background save when already granted (avoid render loops)
  const ranRef = useRef(false);
  useEffect(() => {
    if (detection.context === 'ios_standalone_all_set' && !ranRef.current) {
      ranRef.current = true;
      void alreadyEnabledSaveIfNeeded();
    }
  }, [detection.context]);

  switch (detection.context) {
    case 'in_app_open_in_safari':
      return (
        <Section title="Open this in Safari">
          <div class="install-steps">
            <div class="install-step">
              <span class="num">1</span>
              <span>Tap the ••• or share menu in the top-right.</span>
            </div>
            <div class="install-step">
              <span class="num">2</span>
              <span>Choose “Open in Safari”.</span>
            </div>
          </div>
          <div class="install-actions">
            <button
              class="secondary-btn"
              type="button"
              onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
            >
              Copy link
            </button>
          </div>
        </Section>
      );

    case 'ios_safari_a2hs':
      return (
        <Section title="Add Park Club to your Home Screen">
          <div class="install-steps">
            <div class="install-step">
              <span class="num">1</span>
              <ShareIcon /> <span>Tap the Share button</span>
            </div>
            <div class="install-step">
              <span class="num">2</span>
              <AddToHomeIcon /> <span>Tap “Add to Home Screen”</span>
            </div>
            <div class="install-step">
              <span class="num">3</span>
              <BellIcon /> <span>Open the app and turn on notifications</span>
            </div>
          </div>
        </Section>
      );

    case 'ios_standalone_enable_push':
      return (
        <Section title="Turn on notifications">
          <p class="muted">We’ll only alert you about nearby park meetups.</p>
          <div class="install-actions">
            <button
              class="primary-btn"
              type="button"
              onClick={() => void enableNotifications()}
              disabled={state.kind === 'subscribing'}
            >
              {state.kind === 'subscribing' ? 'Enabling…' : 'Turn on notifications'}
            </button>
          </div>
          {state.kind === 'success' && (
            <>
              <div class="tiny-banner success" role="status">
                You’re all set! You can test below anytime.
              </div>
              <div class="install-actions">
                <button
                  class="secondary-btn"
                  type="button"
                  onClick={() => void requestTestNotification()}
                >
                  Send me a test notification
                </button>
              </div>
            </>
          )}
          {state.kind === 'error' && (
            <div class="tiny-banner error" role="alert">
              {state.message}
              <div class="install-actions" style="margin-top:8px;">
                <button
                  class="secondary-btn"
                  type="button"
                  onClick={() => void enableNotifications()}
                  disabled={false}
                >
                  Try again
                </button>
              </div>
            </div>
          )}
        </Section>
      );

    case 'denied_explain':
      return (
        <Section title="Turn notifications back on">
          <div class="install-steps">
            <div class="install-step">
              <span class="num">1</span>
              <span>Open Settings</span>
            </div>
            <div class="install-step">
              <span class="num">2</span>
              <span>Notifications → Park Club</span>
            </div>
            <div class="install-step">
              <span class="num">3</span>
              <span>Allow Notifications</span>
            </div>
          </div>
        </Section>
      );

    case 'ios_too_old':
      return (
        <Section title="Update iOS to enable notifications">
          <p class="muted">
            Park Club works without notifications. To get alerts on iPhone, update to iOS 16.4 or later.
          </p>
        </Section>
      );

    case 'android_enable':
      return (
        <Section title="Enable notifications">
          <p class="muted">Optional to install. You’ll get meetup alerts.</p>
          <div class="install-actions">
            <button
              class="primary-btn"
              type="button"
              onClick={() => void enableNotifications()}
              disabled={state.kind === 'subscribing'}
            >
              {state.kind === 'subscribing' ? 'Enabling…' : 'Enable notifications'}
            </button>
          </div>
          {state.kind === 'success' && (
            <>
              <div class="tiny-banner success" role="status">
                You’re all set! You can test below anytime.
              </div>
              <div class="install-actions">
                <button
                  class="secondary-btn"
                  type="button"
                  onClick={() => void requestTestNotification()}
                >
                  Send me a test notification
                </button>
              </div>
            </>
          )}
          {state.kind === 'error' && (
            <div class="tiny-banner error" role="alert">
              {state.message}
            </div>
          )}
        </Section>
      );

    case 'desktop_enable':
      return (
        <Section title="Enable notifications (optional install)">
          <p class="muted">You can install this app from your browser menu, or just enable notifications.</p>
          <div class="install-actions">
            <button
              class="primary-btn"
              type="button"
              onClick={() => void enableNotifications()}
              disabled={state.kind === 'subscribing'}
            >
              {state.kind === 'subscribing' ? 'Enabling…' : 'Enable notifications'}
            </button>
          </div>
          {state.kind === 'success' && (
            <>
              <div class="tiny-banner success" role="status">
                You’re all set! You can test below anytime.
              </div>
              <div class="install-actions">
                <button
                  class="secondary-btn"
                  type="button"
                  onClick={() => void requestTestNotification()}
                >
                  Send me a test notification
                </button>
              </div>
            </>
          )}
          {state.kind === 'error' && (
            <div class="tiny-banner error" role="alert">
              {state.message}
            </div>
          )}
        </Section>
      );

    case 'ios_standalone_all_set':
      return (
        <Section title="Notifications are on">
          {state.kind === 'error' && (
            <div class="tiny-banner error" role="alert">
              {state.message}
              <div class="install-actions" style="margin-top:8px;">
                <button class="secondary-btn" type="button" onClick={() => void enableNotifications()}>
                  Try again
                </button>
              </div>
            </div>
          )}
          {state.kind !== 'error' && (
            <>
              <div class="tiny-banner success" role="status">
                You’re all set! You can test below anytime.
              </div>
              <div class="install-actions">
                <button
                  class="secondary-btn"
                  type="button"
                  onClick={() => void requestTestNotification()}
                >
                  Send me a test notification
                </button>
              </div>
            </>
          )}
        </Section>
      );
  }
}

